// ─── La scheda di un corso: chi è iscritto, le sue volte, e come si governa ──
//
// 🎓⭐⭐ **LA COSA CHE QUESTA SCHERMATA DEVE FAR CAPIRE IN UN COLPO: gli iscritti
//    sono UNA cosa sola per tutte le date.** Chi si iscrive entra su tutte le
//    volte che restano; chi si toglie esce da tutte quelle che restano. Le date
//    già passate non si toccano — sono successe.
//
// 🎯 **E l'ASSENZA si legge senza che nessuna tabella la scriva**: accanto a ogni
//    volta c'è quanti c'erano **quel giorno**. «iscritti 6 · questa volta 5» dice
//    da solo che qualcuno è stato tolto da lì, e per rimetterlo o toglierne un
//    altro si apre quella data — che è la scheda della lezione, e c'è già.
// 📌 *Un'assenza è la differenza fra due numeri che esistono già, non un dato
//    nuovo da tenere in un secondo posto.*
//
// 🆕⭐⭐ **IL CORSO SI GOVERNA — 20/09/2026, tappa ③.** Prima di oggi da qui si
//    cambiava **solo la durata**: per rinominarlo, allungarlo o chiuderlo
//    bisognava chiedere alla segreteria, e un corso che continuava il mese dopo
//    si rifaceva da capo riscrivendo a mano tutti gli iscritti.
// 🗣️ È la sua frase a renderlo urgente: *«i corsi di solito hanno un pagamento
//    mensile»*.
// ⚖️ **Cosa resta della segreteria, e la schermata lo dice invece di tacerlo**:
//    il giorno, l'ora e il campo. Governare non è spostare — la prima domanda è
//    *«cos'è questo corso»*, la seconda *«dove lo metto»*, e le rispondono due
//    persone diverse.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ETICHETTE_CADENZA, formatCents, minutiDaOra, oraDaMinuti, type VoltaAnnullata } from '@pmo/shared';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { chiama } from '../../lib/api.js';
import { Attesa, Guasto } from '../../components/pezzi.js';
import { giornoBreve, giornoDellaSettimana, giornoPerEsteso } from '../../lib/time.js';
import { CercaSocio, type SocioTrovato } from './CercaSocio.js';
import { ScegliDurata, scriviDurata } from './Durata.js';
import { FoglioBasso } from './FoglioBasso.js';
import { RigaFatta, RigaFerma } from './Righe.js';
import { rinfrescaLaGiornata } from './pezzi.js';

interface Iscritto {
  id: string;
  displayName: string;
  dal: string;
  totalDueCents: number;
}
interface Volta {
  id: string;
  date: string;
  time: string;
  durationMinutes: number;
  courtName: string;
  status: string;
  inCampo: number;
  assenti: number;
  passata: boolean;
}
/** ⏳ Chi aspetta un posto. I due id sono due gesti diversi (⇒ `inAttesaSchema`). */
interface InAttesa {
  id: string;
  memberId: string;
  displayName: string;
  dal: string;
}
interface MioCorso {
  serieId: string;
  title: string | null;
  cadenza: string;
  dal: string;
  al: string;
  iscritti: Iscritto[];
  attesa: InAttesa[];
  postiLiberi: number;
  volte: Volta[];
}
interface Esito {
  volte: number;
  saltate: Array<{ date: string; perche: string }>;
  corso: MioCorso;
}
interface EsitoVolte {
  nate: number;
  tolte: number;
  saltate: Array<{ date: string; perche: string }>;
  corso: MioCorso;
}

/** Quale foglio è aperto. `null` = nessuno, ed è lo stato normale. */
type Aperto = 'nome' | 'volte' | 'durata' | null;

/**
 * ⚖️ Le pastiglie contano le volte **IN TUTTO**, comprese quelle già fatte: è il
 * numero con cui il corso si vende («un corso da otto»), non un residuo che cala
 * ogni settimana. Quelle che cancellerebbero il passato sono spente, e la riga
 * sotto dice perché.
 */
const QUANTE = [4, 6, 8, 10, 12];

export function Corso(): React.JSX.Element {
  const { id = '' } = useParams();
  const vaiA = useNavigate();
  const coda = useQueryClient();
  const chiave = ['maestro', 'corso', id];
  /* ⭐ L'esito dell'ultimo gesto resta a schermo finché non se ne fa un altro:
     «iscritto su 6 volte» è la conferma che il gesto ha fatto quello che
     sembrava, e «una saltata perché il campo era pieno» è la cosa che, non
     detta, si scopre fra tre settimane. */
  const [ultimo, setUltimo] = useState<{ cosa: string; esito: Esito } | null>(null);
  const [foglio, setFoglio] = useState<Aperto>(null);
  const [nome, setNome] = useState<string | null>(null);
  const [chiedeConferma, setChiedeConferma] = useState(false);

  /* 🔁 Com'è andato l'annullo di una volta, arrivato dalla scheda di quella
     volta: il fatto è successo là, ma si **vede** qui — è qui che l'ultima
     volta si è spostata di una settimana (⇒ `Lezione.tsx`). */
  const daLaggiu = (useLocation().state as { annullo?: VoltaAnnullata } | null)?.annullo ?? null;

  const corso = useQuery({
    queryKey: chiave,
    queryFn: () => chiama<MioCorso>('GET', `/maestro/corsi/${id}`),
  });

  const rinfresca = (cosa: string) => (e: Esito) => {
    coda.setQueryData(chiave, e.corso);
    void coda.invalidateQueries({ queryKey: ['maestro', 'corsi'] });
    // Gli stessi nomi stanno nella giornata: lasciarla com'era mostrerebbe un
    // elenco vecchio a chi torna indietro.
    rinfrescaLaGiornata(coda);
    setUltimo({ cosa, esito: e });
    setFoglio(null);
  };

  const iscrivi = useMutation({
    meta: { fatto: 'allievo iscritto al corso' },
    mutationFn: (memberId: string) => chiama<Esito>('POST', `/maestro/corsi/${id}/iscritti`, { memberId }),
    onSuccess: rinfresca('iscritto'),
  });
  const togli = useMutation({
    meta: { fatto: 'allievo tolto dal corso' },
    mutationFn: (iscrizioneId: string) =>
      chiama<Esito>('DELETE', `/maestro/corsi/${id}/iscritti/${iscrizioneId}`),
    onSuccess: rinfresca('tolto'),
  });

  /**
   * ⏳⭐⭐ **LA LISTA D'ATTESA — tappa ⑦b, 20/09/2026.**
   *
   * 🗣️ Sua decisione del 19/09: **«in attesa, e lo fai entrare tu»**.
   *
   * ⚖️ **Le due rotte rendono il corso e basta**, senza `volte`/`saltate`: una
   * coda non mette nessuno in campo, quindi non c'è niente da saltare e niente
   * da contare. ⇒ Non passano da `rinfresca`, che è la forma dei gesti che
   * *scrivono nelle date* — e il loro esito si vede da sé: la riga compare o
   * sparisce.
   *
   * ⛔ **E non esiste un «fai entrare»**: farlo entrare È iscriverlo, e la sua
   *    riga in coda la toglie il server dentro l'iscrizione stessa. Una
   *    mutazione in più qui vorrebbe dire un secondo modo di far entrare
   *    qualcuno — cioè un secondo posto in cui ricordarsi della coda.
   */
  const inCassa = (c: MioCorso): void => {
    coda.setQueryData(chiave, c);
  };
  const metteInAttesa = useMutation({
    meta: { fatto: 'messo in lista d’attesa' },
    mutationFn: (memberId: string) => chiama<MioCorso>('POST', `/maestro/corsi/${id}/attesa`, { memberId }),
    onSuccess: inCassa,
  });
  const togliDallAttesa = useMutation({
    meta: { fatto: 'tolto dalla lista d’attesa' },
    mutationFn: (attesaId: string) => chiama<MioCorso>('DELETE', `/maestro/corsi/${id}/attesa/${attesaId}`),
    onSuccess: inCassa,
  });

  /**
   * ⏱️⭐⭐ **LA DURATA DI TUTTO IL CORSO, in un gesto** — 18/09/2026, 🗣️ *«mi
   * devi far modificare la durata di una lezione o di un corso»*.
   *
   * ⚖️ **Non è la stessa cosa che aprire una per una le sue volte**, e la
   * differenza non è la comodità: di qui parte **un avviso per persona**, non
   * uno per data (⇒ `cambiaLaDurata`). Sei volte per quattro iscritti allungate
   * a mano sarebbero ventiquattro messaggi per un fatto solo.
   * ⏳ Tocca **solo le volte che restano**: quelle già giocate sono successe
   *    così, e su quelle può esserci già passato del denaro.
   */
  const durata = useMutation({
    meta: { fatto: 'durata del corso cambiata' },
    mutationFn: (durationMinutes: number) =>
      chiama<Esito>('PATCH', `/maestro/corsi/${id}/durata`, { durationMinutes }),
    onSuccess: rinfresca('durata'),
  });

  const rinomina = useMutation({
    /* ⚠️ La risposta NON porta `saltate`, e non è una dimenticanza del server:
       una rinomina non può saltare niente. ⇒ Si aggiunge qui l'elenco vuoto
       invece di far dichiarare al server un campo che per lui è sempre vuoto.
       🩹 Senza questa riga la schermata **spariva** dopo aver salvato il nome —
       trovato col browser, non rileggendo: `esito.saltate.length` su un campo
       che non c'è butta giù tutto il disegno, e le prove scritte non lo
       vedevano perché non disegnano. */
    meta: { fatto: 'nome del corso cambiato' },
    mutationFn: (title: string) => chiama<Omit<Esito, 'saltate'>>('PATCH', `/maestro/corsi/${id}`, { title }),
    onSuccess: (e) => rinfresca('nome')({ ...e, saltate: [] }),
  });

  const quanteVolte = useMutation({
    meta: { fatto: 'numero di lezioni del corso cambiato' },
    mutationFn: (volte: number) => chiama<EsitoVolte>('PATCH', `/maestro/corsi/${id}/volte`, { volte }),
    /* ⚖️ `nate` e `tolte` sono due numeri, e la frase li racconta come due fatti
       diversi: allungare chiede al circolo dei campi che potrebbe non dare,
       accorciare toglie delle ore che erano promesse. */
    onSuccess: (e) => rinfresca(e.nate > 0 ? 'allungato' : 'accorciato')({ ...e, volte: e.nate + e.tolte }),
  });

  const annullaIlCorso = useMutation({
    meta: {
      fatto: (d) => {
        const r = d as { tolte: number; avvisati: number };
        return `corso annullato: ${r.tolte} lezioni tolte, ${r.avvisati} allievi avvisati`;
      },
    },
    mutationFn: () => chiama<{ tolte: number; avvisati: number }>('POST', `/maestro/corsi/${id}/annulla`),
    onSuccess: () => {
      /* 🩹⭐ **PRIMA SI TOGLIE, POI SI RINFRESCA**, e l'ordine è la cura: questa
         scheda è ancora montata, e invalidare `['maestro','corso']` la farebbe
         richiedere al server un corso che non esiste più — un **404 subito dopo
         un gesto riuscito**, misurato col browser. Toglierla dalla cassa la fa
         smettere di chiedere.
         📌 *Una cosa che si sta per lasciare non si aggiorna: si butta.* */
      coda.removeQueries({ queryKey: chiave });
      rinfrescaLaGiornata(coda);
      /* ⭐ Si va via e non si resta: annullate le volte che restavano, questo
         corso non ha più niente da mostrare — il server risponderebbe 404, e un
         404 dopo un gesto riuscito si legge come un guasto.
         ⏳ Se aveva un passato resta nell'elenco, finito: le volte già fatte
            sono successe.
         🚨 E ci si muove **navigando**, mai ricaricando l'indirizzo: il gettone
            vive in memoria, e un ricaricamento riporta alla schermata
            d'accesso (⇒ `docs/banco-locale.md`, trappola ②). */
      vaiA('/maestro/corsi');
    },
  });

  if (corso.isLoading) return <Attesa cosa="il corso" />;
  if (corso.error) return <Guasto errore={corso.error} />;
  const c = corso.data!;
  /* ⏳ Quelle su cui il gesto può ancora agire: è lo stesso insieme che il
     server chiama `volteCheRestano`, e la schermata deve contarne quante ne
     conta lui — o la frase prometterebbe più di quello che succede. */
  const restanti = c.volte.filter((v) => !v.passata && v.status === 'confirmed');
  const fatte = c.volte.length - restanti.length;
  const durate = [...new Set(restanti.map((v) => v.durationMinutes))];
  /** `null` = non durano tutte uguale, e allora nessun bottone è quello acceso. */
  const durataDiAdesso = durate.length === 1 ? durate[0]! : null;
  const ultima = c.volte[c.volte.length - 1];
  const inCorso = rinomina.isPending || quanteVolte.isPending || durata.isPending;

  return (
    <>
      <p>
        <Link className="indietro" to="/maestro/corsi">
          ‹ I tuoi corsi
        </Link>
      </p>

      <section className="riquadro">
        <div className="capo-scheda">
          <h2>{c.title ?? 'Corso'}</h2>
          <span className="pastiglia">corso</span>
        </div>
        <p className="tenue piccolo">
          {ETICHETTE_CADENZA[c.cadenza as keyof typeof ETICHETTE_CADENZA] ?? c.cadenza} ·{' '}
          {restanti.length === 0
            ? 'finito'
            : restanti.length === 1
              ? '1 volta che resta'
              : `${restanti.length} volte che restano`}
        </p>

        {/* 🔁 Com'è finito l'annullo di una volta, arrivato dalla sua scheda. */}
        {daLaggiu !== null && (
          <div className="esito-corso">
            <p>
              {daLaggiu.recuperata !== null
                ? `Annullata, e recuperata alla fine: il corso adesso finisce ${giornoPerEsteso(daLaggiu.recuperata.date)}.`
                : daLaggiu.perche !== null
                  ? `Annullata. Non si è potuta recuperare: ${daLaggiu.perche}`
                  : 'Annullata: una volta in meno.'}
            </p>
          </div>
        )}

        {/* ⭐⭐ LE CINQUE RIGHE: tre si toccano, due dicono di chi sono. Una riga
            spenta senza il suo «lo fa la segreteria» si legge come un guasto —
            chi guarda la preme, non succede niente, e non impara niente. */}
        <div className="righe-maestro">
          <RigaFatta
            che="Come si chiama"
            /* ⚖️ NON mostra il valore: il nome sta già in testa, grande. Una riga
               di comando non deve ripetere il valore quando il valore è il
               titolo della schermata — deve dire **cosa cambia**. */
            valore=""
            apri={() => {
              setNome(c.title ?? '');
              setFoglio('nome');
            }}
          />
          <RigaFatta
            che="Quante volte"
            valore={restanti.length === 0 ? 'finito' : `ancora ${restanti.length}`}
            apri={() => setFoglio('volte')}
          />
          <RigaFatta
            che="Quanto dura"
            ambito="tutte le volte"
            valore={durataDiAdesso === null ? 'diverse' : scriviDurata(durataDiAdesso)}
            apri={() => setFoglio('durata')}
          />
          {/* ⭐ «ogni lunedì · 19:30», non solo l'ora: è la frase con cui il
              corso si nomina a voce, e un orario nudo non dice quando venire. */}
          <RigaFerma
            che="Quando"
            valore={
              c.volte[0] === undefined
                ? '—'
                : `ogni ${giornoDellaSettimana(c.volte[0].date)} · ${c.volte[0].time}`
            }
            chi="lo fa la segreteria"
          />
          <RigaFerma che="Dove" valore={c.volte[0]?.courtName ?? '—'} chi="lo fa la segreteria" />
        </div>
        <p className="regola">
          {restanti.length === 0 ? (
            <>
              Durata e volte cambiano <strong>niente</strong>: il corso è finito.
            </>
          ) : restanti.length === 1 ? (
            <>
              Durata e volte cambiano <strong>l’unica volta che resta</strong>,{' '}
              {giornoPerEsteso(ultima!.date)}.
            </>
          ) : (
            <>
              Durata e volte cambiano <strong>tutte e {restanti.length} le volte che restano</strong>:
              l’ultima è {giornoPerEsteso(ultima!.date)}.
            </>
          )}
        </p>
        {ultimo?.cosa !== 'iscritto' && ultimo?.cosa !== 'tolto' && <RisultatoDelGesto ultimo={ultimo} />}
        <Guasto errore={rinomina.error} />
        <Guasto errore={quanteVolte.error} />
        <Guasto errore={durata.error} />
      </section>

      <section className="riquadro">
        <h2>Chi è iscritto</h2>
        {c.iscritti.length === 0 ? (
          <p className="tenue">
            Ancora nessuno. Chi si iscrive entra su tutte le volte che restano, non solo sulla prima.
          </p>
        ) : (
          <ul className="elenco-allievi">
            {c.iscritti.map((i) => (
              <li key={i.id}>
                <span>
                  {i.displayName}
                  {/* ⏳ «Dal…» solo per chi è entrato a corso cominciato: sugli
                      altri sarebbe una data ripetuta su ogni riga, cioè rumore. */}
                  {i.dal > c.dal && <span className="tenue piccolo"> · dal {giornoPerEsteso(i.dal)}</span>}
                </span>
                {/* 💶 **IL TOTALE DEL CORSO, non la quota di una volta**: il
                    listino divide per quattro fisse, quindi la quota di una volta
                    su un corso da sei non è quello che quella persona pagherà
                    (⇒ `iscrittoAlCorsoSchema`). */}
                <span className="soldi">{formatCents(i.totalDueCents)}</span>
                <button
                  type="button"
                  className="gesto-riga"
                  disabled={togli.isPending}
                  onClick={() => togli.mutate(i.id)}
                >
                  Togli
                </button>
              </li>
            ))}
          </ul>
        )}
        <Guasto errore={togli.error} />
        {/* 🚨 L'esito si legge DOVE si è toccato, e questo riquadro risponde solo
            dei suoi due gesti. 📏 Guardato sullo schermo: cambiando la durata,
            «Cambiata la durata di 4 volte» compariva quassù, dentro gli
            iscritti — a due schermate dal bottone che l'aveva prodotto, cioè
            dove nessuno stava guardando.
            📌 *Una conferma lontana dal gesto non conferma: informa qualcun
               altro.* */}
        {(ultimo?.cosa === 'iscritto' || ultimo?.cosa === 'tolto') && <RisultatoDelGesto ultimo={ultimo} />}
      </section>

      {/* ⏳⭐⭐ **IN ATTESA — e il pezzo che conta è che NON ENTRA NESSUNO DA
          SOLO.** 🗣️ Sua decisione del 19/09: *«in attesa, e lo fai entrare
          tu»*. Quando un posto si libera lo schermo **propone**, e aspetta.
          ⚖️ Un ingresso automatico manderebbe un avviso a un socio per una
             decisione che il maestro non ha preso — e lui se ne accorgerebbe a
             bordo campo, trovandosi uno in più.
          ⛔ **Il riquadro non c'è se la coda è vuota**, che è quasi sempre: una
             sezione «In attesa: nessuno» occupa una schermata di telefono per
             dire che non c'è niente da fare. */}
      {c.attesa.length > 0 && (
        <section className="riquadro">
          <h2>
            In attesa<span className="contatore">{c.attesa.length}</span>
          </h2>
          <ul className="elenco-allievi">
            {c.attesa.map((a, i) => (
              <li key={a.id}>
                <span>{a.displayName}</span>
                {/* ⭐ «il primo» e «poi»: l'ordine è un fatto della coda, e
                    scriverlo è l'unico modo di far vedere che c'è. */}
                <span className="soldi tenue piccolo">{i === 0 ? 'il primo' : 'poi'}</span>
                <button
                  type="button"
                  className="gesto-riga"
                  disabled={togliDallAttesa.isPending}
                  onClick={() => togliDallAttesa.mutate(a.id)}
                >
                  Toglilo
                </button>
              </li>
            ))}
          </ul>
          {c.postiLiberi > 0 ? (
            <>
              {/* ⭐ Il bottone nomina **la persona**: «c'è posto» da solo
                  lascerebbe indovinare chi entra, e chi entra è una decisione,
                  non un automatismo. */}
              <button
                type="button"
                className="aggiungi"
                disabled={iscrivi.isPending}
                onClick={() => iscrivi.mutate(c.attesa[0]!.memberId)}
              >
                <span className="segno">+</span>C’è posto: fai entrare {c.attesa[0]!.displayName}
              </button>
              <p className="regola">
                Entra <strong>solo se lo dici tu</strong>: nessuno si iscrive da sé mentre non guardi.
              </p>
            </>
          ) : (
            <p className="regola">
              Appena si libera un posto te lo propongo qui: <strong>non entra nessuno da solo</strong>.
            </p>
          )}
          <Guasto errore={togliDallAttesa.error} />
        </section>
      )}

      {/* 🔎⭐ **LA STESSA RICERCA, DUE DESTINAZIONI.** A corso pieno iscrivere
          non iscriverebbe: il server salterebbe **tutte** le volte col motivo
          «il campo è pieno», e chi guarda leggerebbe un elenco di rifiuti al
          posto di un gesto riuscito. ⇒ Quando non c'è posto, di qui si entra in
          **coda**, e il titolo lo dice prima del tocco.
          ⚠️ `giaDentro` cambia con la destinazione, e non è un dettaglio: con
             un posto libero, chi è **in attesa** deve restare acceso — sceglierlo
             lì è esattamente il gesto che lo fa entrare. */}
      {c.postiLiberi > 0 ? (
        <CercaSocio
          titolo="Iscrivi qualcuno"
          giaDentro={c.iscritti.map((i) => i.displayName)}
          comeCiSta="già iscritto"
          scegli={(s: SocioTrovato) => iscrivi.mutate(s.id)}
          inCorso={iscrivi.isPending}
          errore={iscrivi.error}
        />
      ) : (
        <CercaSocio
          titolo="Il corso è pieno: metti in attesa"
          giaDentro={[...c.iscritti.map((i) => i.displayName), ...c.attesa.map((a) => a.displayName)]}
          comeCiSta="già in questo corso"
          scegli={(s: SocioTrovato) => metteInAttesa.mutate(s.id)}
          inCorso={metteInAttesa.isPending}
          errore={metteInAttesa.error}
        />
      )}

      <section className="riquadro">
        <h2>
          Le volte
          <span className="contatore">
            {restanti.length === 1 ? '1 che resta' : `${restanti.length} che restano`}
          </span>
        </h2>
        <ul className="elenco-volte">
          {c.volte.map((v) => (
            <li key={v.id} className={v.passata ? 'passata' : ''}>
              <Link to={`/maestro/lezioni/${v.id}`}>
                <strong>{giornoPerEsteso(v.date)}</strong>
                <span className="tenue">
                  {v.time}–{oraDaMinuti(minutiDaOra(v.time) + v.durationMinutes)} · {v.courtName}
                </span>
                {/* 🔄🚨 QUI C'ERA UN CONFRONTO, e diceva l'assenza per
                    differenza: «iscritti 6 · questa volta 5». ⇒ Non regge più,
                    e non per come si scrive: quella differenza si vedeva
                    perché togliere qualcuno gli **cancellava la riga** — cioè
                    perché il suo conto calava, che è la cosa che lui ha
                    deciso di non voler fare. Adesso l'assente resta fra i
                    giocatori, e l'assenza è un numero suo.
                    📌 *Un'assenza dedotta da una differenza si vede solo
                       finché l'assente sparisce.* */}
                <span className={v.assenti === 0 ? 'tenue piccolo' : 'piccolo diverso'}>
                  {v.assenti === 0
                    ? `in campo ${v.inCampo}`
                    : `in campo ${v.inCampo} · ${v.assenti} ${v.assenti === 1 ? 'assente' : 'assenti'}`}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* ⛔⭐⭐ L'AZIONE CHE NON SI DISFA STA IN FONDO ALLO SCORRIMENTO, larga e
          rossa — non in una barra ferma sotto il pollice accanto agli altri
          comandi. E la conferma dice **cosa** annulla, con il numero dentro. */}
      {restanti.length > 0 && (
        <section className="riquadro">
          {chiedeConferma ? (
            <div className="conferma-rossa">
              <p>
                Annullo il corso e{' '}
                {restanti.length === 1
                  ? 'l’unica volta che resta'
                  : `tutte le ${restanti.length} volte che restano`}
                ?
              </p>
              <button
                type="button"
                className="pericolo"
                disabled={annullaIlCorso.isPending}
                onClick={() => annullaIlCorso.mutate()}
              >
                Sì, annulla
              </button>
              <button type="button" className="piatto" onClick={() => setChiedeConferma(false)}>
                No, lascia stare
              </button>
            </div>
          ) : (
            <button type="button" className="pericolo-largo" onClick={() => setChiedeConferma(true)}>
              Annulla il corso
            </button>
          )}
          <p className="regola">
            {fatte === 0
              ? 'Chi è iscritto viene avvisato: un messaggio a testa, non uno per data.'
              : `Le ${fatte === 1 ? 'volta già fatta resta' : `${fatte} volte già fatte restano`}: sono successe. Chi è iscritto viene avvisato.`}
          </p>
          <Guasto errore={annullaIlCorso.error} />
        </section>
      )}

      {foglio === 'nome' && (
        <FoglioBasso titolo="Come si chiama" chiudi={() => setFoglio(null)}>
          <div className="campo">
            <input
              type="text"
              aria-label="Come si chiama il corso"
              value={nome ?? ''}
              autoFocus
              onChange={(e) => setNome(e.target.value)}
            />
          </div>
          <ul className="elenco-scelte">
            <li>
              <button
                type="button"
                disabled={inCorso || (nome ?? '').trim().length < 2}
                onClick={() => rinomina.mutate((nome ?? '').trim())}
              >
                Salva il nome
              </button>
            </li>
          </ul>
          {/* 🔇 Va detto, o si crede il contrario: fra i sette gesti non ce n'è
              uno per «si chiama diversamente», e non se ne inventa un ottavo per
              cambiare una frase (⇒ `avvisi.ts`). */}
          <p className="regola">
            Cambia il nome <strong>su tutte le volte</strong>, anche quelle già fatte — o l’elenco dei corsi
            continuerebbe a mostrare il nome vecchio. Agli iscritti <strong>non arriva niente</strong>.
          </p>
          <Guasto errore={rinomina.error} />
        </FoglioBasso>
      )}

      {foglio === 'volte' && (
        <FoglioBasso titolo="Quante volte" chiudi={() => setFoglio(null)}>
          <div className="durate-maestro quante" role="group" aria-label="Quante volte in tutto">
            {[...new Set([...QUANTE, c.volte.length])]
              .sort((a, b) => a - b)
              .map((n) => (
                <button
                  key={n}
                  type="button"
                  className={n === c.volte.length ? 'scelto' : ''}
                  aria-pressed={n === c.volte.length}
                  /* ⛔ Sotto le volte già fatte non si scende: cancellerebbe il
                     passato, che è la sola cosa che nessun gesto di quest'area
                     può fare. */
                  disabled={inCorso || n < fatte}
                  onClick={() => quanteVolte.mutate(n)}
                >
                  {n}
                </button>
              ))}
          </div>
          <p className="regola">
            {restanti.length === 0 ? (
              'Il corso è finito.'
            ) : fatte === 0 ? (
              <>
                In tutto {c.volte.length} volte: l’ultima è <strong>{giornoPerEsteso(ultima!.date)}</strong>.
              </>
            ) : (
              <>
                In tutto, <strong>comprese le {fatte} già fatte</strong>. Ne{' '}
                {restanti.length === 1 ? 'resta 1' : `restano ${restanti.length}`}: l’ultima è{' '}
                <strong>{giornoPerEsteso(ultima!.date)}</strong>.
              </>
            )}
          </p>
          <Guasto errore={quanteVolte.error} />
        </FoglioBasso>
      )}

      {foglio === 'durata' && (
        <FoglioBasso titolo="Quanto dura" chiudi={() => setFoglio(null)}>
          <ScegliDurata durata={durataDiAdesso} scegli={(d) => durata.mutate(d)} disabilitato={inCorso} />
          <p className="regola">
            {durataDiAdesso === null
              ? /* ⚠️ Le volte che restano non durano tutte uguale — di solito
                   perché qualcuna è stata ritoccata da sola. Nessun bottone è
                   acceso, e la frase dice perché invece di lasciarlo sembrare un
                   difetto. */
                'Le volte che restano non durano tutte uguale. Toccandone una, diventano tutte così.'
              : `Cambia ${restanti.length === 1 ? 'la volta che resta' : `tutte e ${restanti.length} le volte che restano`}, comprese quelle cambiate una per una. Quelle passate no.`}
          </p>
          <Guasto errore={durata.error} />
        </FoglioBasso>
      )}
    </>
  );
}

/**
 * ⭐ Cos'è successo davvero all'ultimo gesto.
 *
 * ⚠️ **Le volte saltate si dicono col motivo**, ed è la stessa regola della
 * creazione di una serie: una scrittura parziale si accetta solo se chi l'ha
 * chiesta sa **quale metà** è mancata. Un «fatto» su un'iscrizione riuscita a
 * metà è il modo di scoprire il buco fra tre settimane, sul campo.
 */
function RisultatoDelGesto({
  ultimo,
}: {
  ultimo: { cosa: string; esito: Esito } | null;
}): React.JSX.Element | null {
  if (ultimo === null) return null;
  const { cosa, esito } = ultimo;
  const volte = (n: number): string => (n === 1 ? '1 volta' : `${n} volte`);
  return (
    <div className="esito-corso">
      <p>
        {cosa === 'iscritto'
          ? `Iscritto, e messo in campo su ${volte(esito.volte)}.`
          : cosa === 'tolto'
            ? `Tolto da ${volte(esito.volte)}. Quelle già passate restano come sono.`
            : cosa === 'nome'
              ? `Adesso si chiama «${esito.corso.title ?? 'Corso'}».`
              : cosa === 'allungato'
                ? `Allungato di ${volte(esito.volte)}: fino a ${giornoBreve(esito.corso.al)}.`
                : cosa === 'accorciato'
                  ? esito.volte === 0
                    ? 'Non è cambiato niente: era già così.'
                    : `Accorciato di ${volte(esito.volte)}: adesso finisce ${giornoBreve(esito.corso.al)}.`
                  : /* ⏱️ Zero non è un guasto: le volte che restano duravano già
                       così, e dirlo è meglio di «cambiate 0 volte», che fa
                       cercare l'errore. */
                    esito.volte === 0
                    ? 'Nessuna volta da cambiare: duravano già così.'
                    : `Cambiata la durata di ${volte(esito.volte)}. Quelle già passate restano come sono.`}
      </p>
      {esito.saltate.length > 0 && (
        <ul className="saltate">
          {esito.saltate.map((s) => (
            <li key={s.date}>
              {giornoPerEsteso(s.date)}: {s.perche}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
